import { Box, Flex, Image, Input, InputGroup, Link, Show } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { useLogin } from '@/hooks/service-hooks/auth';
import { trackCoreClick } from '@/utils/analytics';
import { processLoginData } from '@/utils/processLoginData';

const mindsightLogo = '/svgs/mindsightLogo.svg';
const mindsightLogoWhite = '/svgs/mindsightLogoWhite.svg';

const loginSchema = yup.object().shape({
	email: yup.string().required('Email is required'),
	password: yup.string().required('Password is required'),
});

type LoginFormData = yup.InferType<typeof loginSchema>;

const LoginForm = ({
	setLoginStep,
	setPrefilledEmail,
}: {
	setLoginStep: (step: number) => void;
	setPrefilledEmail: (email: string) => void;
}) => {
	const { colorMode } = useColorMode();
	const darkMode = colorMode === 'dark';
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormData>({
		resolver: yupResolver(loginSchema),
		mode: 'onSubmit',
	});

	const { mutate: loginUser, isLoading: isLoggingIn } = useLogin(
		async (data) => {
			setLoginStep(1);
			
			if (data?.tokens?.access_token) {
				const success = await processLoginData(data);
				if (success) {
					if (data?.user?.userInformation?.isFirstLogin) {
						setTimeout(() => {
							setLoginStep(5);
						}, 500);
					} else {
						setTimeout(() => {
							navigate('/choose-platform', { replace: true });
						}, 1500);
					}
				}
			} else {
				if (data?.email) {
					setPrefilledEmail(data.email);
				}
				setTimeout(() => {
					setLoginStep(2);
				}, 1500);
			}
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Something went wrong. Please try again.',
			);
		},
	);

	const onSubmit = (data: LoginFormData) => {
		trackCoreClick('login_clicked', {
			source_module: 'login_form',
		});
		loginUser({ username: data.email, password: data.password });
	};

	return (
		<Box>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Flex flexDirection="column" gap={1}>
					<Flex justifyContent="center" mt={8} mb={14}>
						<Image
							objectFit="contain"
							src={darkMode ? mindsightLogoWhite : mindsightLogo}
							alt="Mindsight Logo"
							width={'240px'}
							height={'65px'}
						/>
					</Flex>
					<Flex flexDirection="column" gap={2} mb={2}>
						<Box>
							<Input
								disabled={isLoggingIn}
								{...register('email')}
								bg="brand.inputBg"
								variant="outline"
								size="md"
								placeholder="Email address"
								_placeholder={{
									color: { _dark: 'brand.lightGray' },
								}}
								borderColor={errors.email ? 'brand.errors' : undefined}
							/>
							<Show when={errors.email}>
								<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
									{errors.email?.message}
								</FaceliftText>
							</Show>
						</Box>
						<Box>
							<InputGroup
								endElement={
									<FaceliftIconButton
										bg="transparent"
										variant={'ghost'}
										aria-label="toggle password visibility"
										size="xs"
										onClick={() => setShowPassword(!showPassword)}
										_hover={{
											bg: { _dark: 'gray.800', _light: 'gray.300' },
										}}
										color={{ _dark: 'brand', _light: 'brand.500' }}
									>
										{showPassword ? (
											<LuEye size={16} />
										) : (
											<LuEyeOff size={16} />
										)}
									</FaceliftIconButton>
								}
							>
								<Input
									disabled={isLoggingIn}
									{...register('password')}
									variant="outline"
									bg="brand.inputBg"
									size="md"
									placeholder="Password"
									_placeholder={{
										color: { _dark: 'brand.lightGray' },
									}}
									type={showPassword ? 'text' : 'password'}
									borderColor={errors.password ? 'brand.errors' : undefined}
								/>
							</InputGroup>
							<Show when={errors.password}>
								<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
									{errors.password?.message}
								</FaceliftText>
							</Show>
						</Box>
						<Flex justifyContent="flex-end">
							<Link
								onClick={() => navigate('/forgot-password')}
								color={{ _dark: 'brand.lightGray', _light: 'brand.solid' }}
								fontSize="xs"
								fontWeight="bold"
								cursor="pointer"
								textDecoration="underline"
							>
								Forgot Password?
							</Link>
						</Flex>
						<FaceliftButton
							mt={10}
							type="submit"
							borderRadius="6px"
							size="md"
							transition="all 0.3s ease"
							_hover={{
								boxShadow: `0 0 10px 3px`,
								border: 'none',
								transform: 'scale(1.02)',
							}}
							letterSpacing={'widest'}
							loading={isLoggingIn}
							disabled={isLoggingIn}
						>
							LOGIN
						</FaceliftButton>
						{/* TODO: Temporary commented out */}
						{/* <Box>
							<Text
								fontSize="xs"
								color="brand.textWhite"
								fontWeight="light"
								textAlign="center"
								mt={2}
							>
								Don't have an account?{' '}
								<Text
									as="span"
									fontWeight="bold"
									textShadow="0 1px 3px rgba(0, 0, 0, 0.3)"
								>
									Request here
								</Text>
							</Text>
						</Box> */}
					</Flex>
				</Flex>
			</form>

			{/* TODO: Temporary commented out */}
			{/* <Show when={showEnvironmentAccessError}>
				<FaceliftMessage
					timeout={10000}
					error={true}
					messageTitle="Environment Access Denied"
					message="You do not have access to this environment. Please contact your administrator."
					onClose={() => setShowEnvironmentAccessError(false)}
				/>
			</Show> */}
		</Box>
	);
};

export default LoginForm;
